import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  RARITY_STR_TO_NUM_MAP,
} from "@dndModels/Constants";
import { 
  DisablableOption, 
  ModelBase,
  PlayerObject
} from "@models";
import { 
  ARMOR_TYPE_OPTIONS,
  CharacterItem,
  CharacterOptions,
  CharacterSpell,
  LANGUAGE_OPTIONS,
  SENSE_OPTIONS,
  SKILL_OPTIONS,
  WEAPON_KEYWORD_OPTIONS, 
} from "@dndModels";
import { 
  CONSTANTIZED_DATA_TYPES,
  FIELD_DISPLAY_MAP, 
  FIELDS_FOR_DATA_TYPE, 
  INT_CONSTANTS, 
  IS_SINGLE,
  PLAYER_OBJECT_TYPES,
  USE_BOOLEAN, 
  USE_CONSTANT_LIST,  
  USE_ID, 
  USE_INT, 
  USE_STRING 
} from "@dndModels/DataTypeConstants";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import StringBox from "@components/input/strings/StringBox";
import DisplayObjects from "@dndComponents/display/DisplayObjects";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import ButtonBox from "@components/input/buttons/ButtonBox";
import FilterField from "@dndComponents/filtering/FilterField";
import LoadingBox from "@components/input/LoadingBox";
import { isDataType } from '@dndModels/Types';

interface Props {
  fields?: string[];
  flat?: boolean;
  data_type?: string;
  extra_fields?: string[];
  quick_fields?: string[];
  quick_fields_width?: 1 | 2 | 3 | 4;
  dont_use_api?: boolean;
  character_options?: CharacterOptions;
  the_filter?: any;
  disabled?: boolean;
  log_it?: boolean;
  all_objects?: (ModelBase | PlayerObject | CharacterSpell | CharacterItem)[];
  onChange?: (filters: any) => void;
  onLoad?: (filters: any) => void;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

export default function FilterBox({ 
  fields, 
  flat = false,
  data_type,
  extra_fields = [],
  quick_fields = [],
  quick_fields_width = 4,
  dont_use_api,
  character_options,
  the_filter,
  disabled = false,
  log_it = false,
  all_objects,
  onChange,
  onLoad
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [my_filters, setFilters] = useState<{ [field: string]: string[] }>(dont_use_api ? { "search_string": [""] } : {...api.filters,"search_string": [api.search_string]});
  const [my_excludes, setExcludes] = useState<string[]>(dont_use_api ? [] : api.excludes);
  const [up_to_date, setUpToDate] = useState(true);
  const [use_fields, setUseFields] = useState<string[]>([]);
  const [options_for_fields, setOptionsForFields] = useState<{ [field: string]: DisablableOption[] }>({});
    
  const get_filters = useCallback((new_filters: { [field: string]: string[] }, new_excludes: string[], new_fields: string[]) => {
    const the_filters: any = {...new_filters};
    if (log_it) {
      debugger;
    }
    for (const field of Object.keys(new_filters)) {
      if (!new_fields.includes(field) && field !== "search_string") {
        delete the_filters[field];
      }
    }
    for (const field of Object.keys(the_filters)) {
      if (field === "rarity") {
        the_filters.rarity = the_filters.rarity.map((o: string) => +RARITY_STR_TO_NUM_MAP[o]);
      }
      if (field === "search_string") {
        if (the_filters.search_string[0] !== "") {
          if (data_type === "character") {
            the_filters["or"] = [{ "name*includes": the_filters.search_string[0] }];
          } else {
            const search_string = the_filters.search_string[0] as string;
            const pieces = search_string.split("|");
            the_filters["and"] = pieces.map(o => {
              return { "or": [{ "name*includes": o }, { "description*includes": o }]};
            });
          }
        }
        delete the_filters.search_string;
      } else if (USE_BOOLEAN.includes(field)) {
        if (typeof the_filters[field][0] === "string") {
          the_filters[field] = the_filters[field][0].toLocaleLowerCase() === "true";
        }
      } else if (USE_INT.includes(field)) {
        the_filters[field] = +the_filters[field][0];
      } else if (USE_STRING.includes(field)) {
        the_filters[field] = the_filters[field][0];
      } else if (IS_SINGLE.includes(field)) {
        the_filters[field] = the_filters[field][0];
      } else if (USE_ID.includes(field)) {
        // We're filtering by variant_group_id, 
        // but the variant_group_id isn't stored on things that use them, 
        // so we need to go through each one that's selected, and get the full list of ids,
        // and give those to it.
        const all_ids: string[] = [];
        const selected = the_filters[field];
        if (CONSTANTIZED_DATA_TYPES.includes(field)) {
          all_ids.push(...selected);
        } else {
          for (const variant_group_id of selected) {
            let model_bases: ModelBase[] = [];
            if (api.smart_hash[field]) {
              model_bases = api.smart_hash[field].filter(o => o._id === variant_group_id || o.variant_ids.includes(variant_group_id));
            } else if (api.temp_hash[field]) {
              model_bases = api.temp_hash[field].objects.filter(o => o._id === variant_group_id || o.variant_ids.includes(variant_group_id));
            }
            all_ids.push(...model_bases.map(o => o._id));
          }
        }
        the_filters[field] = { "$in": all_ids };
      } else {
        the_filters[field] = { "$in": the_filters[field] };
      }
    }
    for (const field of new_excludes) {
      if (new_fields.includes(field)) {
        if (USE_STRING.includes(field)) {
          if (the_filters[field]) {
            the_filters[field] = { "$nin": the_filters[field] };
          } else {
            the_filters[field] = "**EMPTY**";
          }
        } else {
          if (the_filters[field]) {
            if (the_filters[field]["$in"]) {
              the_filters[field] = { "$nin": the_filters[field]["$in"] };
            } else if (the_filters[field]["$nin"] === undefined) {
              the_filters[field] = { "$nin": the_filters[field] };
            }
          }
        }
      }
    }
    if (data_type && FIELDS_FOR_DATA_TYPE[data_type]) {
      const field_map = FIELDS_FOR_DATA_TYPE[data_type].field_map;
      if (field_map) {
        const keys = Object.keys(field_map);
        // debugger;
        for (const key of keys) {
          if (the_filters[key] !== undefined) {
            the_filters[field_map[key]] = the_filters[key];
            delete the_filters[key];
          }
        }
      }
    }
    return the_filters;
  }, [data_type, log_it]);

  const load = useCallback( async () => {
    // if (log_it) {
    //   debugger;
    // }
    // console.log(the_filter);
    let my_fields = fields ? fields : (data_type && FIELDS_FOR_DATA_TYPE[data_type] ? FIELDS_FOR_DATA_TYPE[data_type].fields : []);
    for (const field of extra_fields) {
      if (!my_fields.includes(field)) {
        my_fields.push(field);
      }
    }
    // debugger;
    if (all_objects && data_type) {
      const remove_us: string[] = [];
      const new_options_for_fields: { [field: string]: DisablableOption[] } = {};
      const dt_info = FIELDS_FOR_DATA_TYPE[data_type];
      if (data_type !== "source_book") {
        my_fields.push("source_book");
      }
      for (const field of my_fields) {
        if (USE_STRING.includes(field)) {
          // Leave it
        } else if (extra_fields.includes(field)) {
          // Leave it
        } else {
          // if (field === "rarities") {
          //   debugger;
          // }
          // Go through each possibility for the field
          // Get all the matching possibilities from all_objects
          let possibilities: DisablableOption[] = [];
          const mapped_field_name = dt_info && dt_info.field_map[field] ? dt_info.field_map[field] : field;
          const used = common_util.unique_array((all_objects as any[]).flatMap(o => o[mapped_field_name])).map(o => `${o}`);
          if (used.length < 2) {
            // If 1 or less possibilities, remove the field.
            remove_us.push(field);
          } else {
            if (USE_BOOLEAN.includes(field)) {
            } else {
              if (USE_ID.includes(field)) {
                if (CONSTANTIZED_DATA_TYPES.includes(field)) {
                  switch (field) {
                    case "armor_type": possibilities = ARMOR_TYPE_OPTIONS; break;
                    case "language": possibilities = LANGUAGE_OPTIONS; break;
                    case "sense": possibilities = SENSE_OPTIONS; break;
                    case "skill": possibilities = SKILL_OPTIONS; break;
                    case "weapon_keyword": possibilities = WEAPON_KEYWORD_OPTIONS; break;
                  }
                } else {
                  if (isDataType(field)) {
                    const new_filter: any = { };
                    if (character_options) {
                      if (field === "source_book") {
                        if (!new_filter._id) {
                          new_filter._id = { "$in": character_options.source_books };
                        }
                      } else {
                        if (!new_filter.source_books) {
                          new_filter.source_books = {};
                          new_filter.source_books.id = { "$in": character_options.source_books };
                        }
                        if (!new_filter._id) {
                          if (field === "race") {
                            if (character_options.blocked && character_options.blocked.length > 0) {
                              new_filter._id = { "$nin": character_options.blocked };
                            }
                          } else if (field === "game_class") {
                            if (character_options.blocked.length > 0) {
                              new_filter._id = { "$nin": character_options.blocked };
                            }
                          } else if (field === "subclass") {
                            if (character_options.blocked.length > 0) {
                              new_filter._id = { "$nin": character_options.blocked };
                            }
                          } else if (field === "gift") {
                            if (character_options.blocked.length > 0) {
                              new_filter._id = { "$nin": character_options.blocked };
                            }
                          }
                        }
                      }
                    }
                    setLoading(true);
                    let objects = await api.getObjects({ data_type: field });
                    if (objects.length > 0 && objects[0] instanceof ModelBase) {
                      objects = (objects as ModelBase[]).filter(o => o.is_true_dominant);
                    }
                    objects = api.filter(field, objects, new_filter);

                    possibilities = objects.map(o => {
                      const option: DisablableOption = {
                        id: o._id,
                        text: o.name
                      };
                      return option;
                    });
                  }
                }
              } else if (USE_INT.includes(field)) {
                possibilities = INT_CONSTANTS[field].map(o => {
                  if (typeof o === "string") {
                    const option: DisablableOption = {
                      id: o,
                      text: o
                    };
                    return option;
                  } else {
                    return o;
                  }
                });
              } else {
                const constant_list = USE_CONSTANT_LIST[field];
                if (constant_list) {
                  possibilities = constant_list.map(o => {
                    if (typeof o === "string") {
                      const option: DisablableOption = {
                        id: o,
                        text: o
                      };
                      return option;
                    } else {
                      return o;
                    }
                  });
                } else {
                  // if (field === "tags") {
                  //   debugger;
                  // }
                  possibilities = used.map(o => {
                    const option: DisablableOption = {
                      id: o,
                      text: o
                    };
                    return option;
                  });
                }
              }
              // Filter the unused possibilities out. 
              possibilities = possibilities.filter(o => used.includes(o.id));

              // If that leaves 1 or less possibilities, remove the field.
              if (possibilities.length < 2) {
                remove_us.push(field);
              } else {
                new_options_for_fields[field] = possibilities;
              }
            }
          }
        }
      }
      my_fields = my_fields.filter(o => !remove_us.includes(o));
      setOptionsForFields(new_options_for_fields);
    }
    setUseFields(my_fields);
    if (the_filter) {
      const new_filters: any = {};
      const new_excludes: string[] = [];
      const keys = Object.keys(the_filter);
      for (const key of keys) {
        if (key === "search_string") {
          if (typeof(the_filter.search_string) === "object") {
            new_filters.search_string = the_filter.search_string;
          } else {
            new_filters.search_string = [the_filter.search_string];
          }
        } else {
          const value = the_filter[key];
          if (value !== undefined) {
            if (USE_BOOLEAN.includes(key)) {
              new_filters[key] = value ? ["True"] : ["False"];
            } else if (USE_INT.includes(key) || USE_STRING.includes(key)) {
              new_filters[key] = value;
            } else if (value["$nin"] !== undefined) {
              new_excludes.push(key);
              new_filters[key] = value["$nin"];
            } else {
              new_filters[key] = value["$in"];
            }
          }
        }
      }
      if (!new_filters.search_string || new_filters.search_string.length === 0) {
        new_filters.search_string = [""];
      }
      setFilters(new_filters);
      setExcludes(new_excludes);
    }
    if (onLoad) {
      onLoad(get_filters(my_filters, my_excludes, my_fields));
    }
    setLoading(false);
  }, [the_filter, get_filters, log_it, fields, all_objects, data_type, extra_fields, character_options, onLoad]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      load();
    }
  }, [fetched, load]);

  let use_filter = the_filter;
  if (!the_filter) {
    use_filter = {};
    const filters = api.get_filters();
    for (const field of use_fields) {
      use_filter[field] = filters[field];
    }
  }

  const setFilter = (field: string, values: string[], exclude: boolean, do_change = false) => {
    const new_filters = {...my_filters};
    let new_excludes = [...my_excludes];
    // console.log(field);
    if (field !== "search_string" && values.length === 0) {
      delete new_filters[field];
      new_excludes = new_excludes.filter(o => o !== field);
      if (!dont_use_api) {
        api.set_exclude(field, false);
      }
    } else {
      new_filters[field] = values;
    }
    if (!dont_use_api) {
      api.set_filter(field, values);
    }
    if (exclude) {
      new_excludes.push(field);
    } else {
      new_excludes = new_excludes.filter(o => o !== field);
    }
    if (!dont_use_api) {
      api.set_exclude(field, exclude);
    }
    setExcludes(new_excludes);
    setFilters(new_filters);
    if (do_change && onChange) {
      onChange(get_filters(new_filters, new_excludes, use_fields));
    } else {
      setUpToDate(false);
    }
  }

  const renderFromConstantList = (constant_list: (string | DisablableOption)[], display: string[]) => {
    if (typeof(constant_list[0]) === "string") {
      return display.join(", ");
    } else {
      const option_list = constant_list as DisablableOption[];
      const finder = option_list.find(o => display.includes(o.id));
      if (finder) {
        return finder.text;
      }
    }
    return null;
  }

  const renderFilters = () => {
    if (flat) {
      if (open) {
        return "Hide Filters";
      } else {
        return "Show Filters";
      }
    } else {
      // debugger;
      let using_fields = Object.keys(my_filters).filter(o => use_fields.includes(o));
      const remove_us: string[] = [];
      for (const field of using_fields) {
        if (USE_BOOLEAN.includes(field)) {
          // debugger;
          if (my_filters[field].length === 0) {
            remove_us.push(field);
          }
        } else if (USE_INT.includes(field)) {
          if (my_filters[field].length === 0) {
            remove_us.push(field);
          }
        } else if (USE_STRING.includes(field)) {
          if (my_filters[field].length === 0 || my_filters[field][0] === "") {
            remove_us.push(field);
          }
        } else {
          if (!my_filters[field]) {
            remove_us.push(field);
          }
        }
      }
      using_fields = using_fields.filter(o => !remove_us.includes(o));
      const empty_string_fields = my_excludes.filter(o => USE_STRING.includes(o) && !using_fields.includes(o));
      using_fields = [...using_fields,...empty_string_fields];
      if (using_fields.length > 0) {
        return using_fields.map((field, key) => {
          const display = my_filters[field];
          const exclude = my_excludes.includes(field);
          if (log_it) {
            console.log(display);
            console.log(exclude);
            // debugger;
          }
          if (display) {
            return (
              <span key={key}>{ key > 0 && ", " }{ FIELD_DISPLAY_MAP[field] }:&nbsp;
                { exclude && (!USE_STRING.includes(field) || !empty_string_fields.includes(field)) && "Not "}
                { USE_ID.includes(field) && isDataType(field) ? 
                  <DisplayObjects type={field} ids={display} />
                : USE_CONSTANT_LIST[field] ?
                  renderFromConstantList(USE_CONSTANT_LIST[field], display)
                : USE_BOOLEAN.includes(field) ?
                  <span>{ display[0] }</span>
                : USE_INT.includes(field) ?
                  <span>{ display[0] }</span>
                : USE_STRING.includes(field) && empty_string_fields.includes(field) ?
                  <span>Empty</span>
                : USE_STRING.includes(field) ?
                  <span>{ display[0] }</span>
                : typeof(display) === "object" && display.length !== undefined ?
                  <span>{ display.join(", ") }</span>
                :
                  <span>{ display }</span>
                } 
              </span>
            );
          }
          return null;
        });
      } else {
        return (
          <span>
            No Filters
          </span>
        );
      }
    }
  };

  const renderExtras = () => {
    if (flat) {
      if (open) {
        return renderExtrasContent();
      } else {
        return null;
      }
    } else {
      return (
        <ModalOrDrawer
          open={ open } 
          onClose={() => {
            setOpen(false);
            if (!up_to_date) {
              if (onChange) {
                onChange(get_filters(my_filters, my_excludes, use_fields));
              }
              setUpToDate(true);
            }
          }}>
          { renderExtrasContent() }
        </ModalOrDrawer>
      );
    }
  };

  const renderExtrasContent = () => {
    // if (log_it) {
    //   debugger;
    // }
    return (
      <Grid container spacing={0} direction="row">
        { !flat &&
          <Grid
            style={{ fontWeight: "bold" }}
            size={{
              xs: 12
            }}>
            Filters
          </Grid>
        }
        { open && use_fields.map((field) => {
          if (!quick_fields.includes(field) && field !== "source_book" && field !== "is_dominant") {
            return renderOption(field, 12, use_filter);
          }
          return null;
        })}
        <Grid
          size={{
            xs: 12
          }}>
          <ButtonBox
            name="Filter"
            disabled={up_to_date}
            onClick={() => {
              if (onChange) {
                onChange(get_filters(my_filters, my_excludes, use_fields));
              }
              setUpToDate(true);
            }}
          />
        </Grid>
      </Grid>
    );
  }

  const renderOption = (field: string, xs: 12 | 6 | 4 | 3, use_filter: any, quick = false) => {
    return (
      <Grid
        key={field}
        size={{
          xs: xs
        }}>
        <Grid container spacing={0} direction="row" className="padded">
          <FilterField 
            field={field} 
            the_filter={use_filter}
            options_for_fields={options_for_fields}
            quick={quick} 
            dont_use_api={dont_use_api}
            log_it={log_it}
            // log_it={field === "sidekick"}
            character_options={character_options}
            onChange={(changed: string[], exclude: boolean) => {
              setFilter(field, changed, exclude, quick);
            }}
          />
        </Grid>
      </Grid>
    );
  }
  
  // console.log(fetched);
  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    const use_quick_fields = quick_fields.filter(o => use_fields.includes(o));
    const remainder = use_quick_fields.length % quick_fields_width;
    const full = use_quick_fields.length - remainder;

    const renderCommonQuickFields = () => {
      if (data_type && !PLAYER_OBJECT_TYPES.includes(data_type)) {
        if (use_fields.includes("source_book") && use_fields.includes("is_dominant")) {
          return (
            <>
              { renderOption("source_book", 6, use_filter, true) }
              { renderOption("is_dominant", 6, use_filter, true) }
            </>
          );
        } else if (use_fields.includes("source_book")) {
          return (
            <>
              { renderOption("source_book", 12, use_filter, true) }
            </>
          );
        } else if (use_fields.includes("is_dominant")) {
          return (
            <>
              { renderOption("is_dominant", 12, use_filter, true) }
            </>
          );
        } 
      }
      return null;
    }
    
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 6
          }}>
          <StringBox
            name="Search"
            value={my_filters.search_string[0]}
            disabled={disabled}
            onBlur={(changed: string) => {
              setFilter("search_string", [changed], false, true);
            }}
          />
        </Grid>
        <Grid
          className={ disabled ? "header" : "header clickable" }
          onClick={() => {
            if (!disabled) {
              if (open && !up_to_date) {
                if (onChange) {
                  onChange(get_filters(my_filters, my_excludes, use_fields));
                }
                setUpToDate(true);
              }
              setOpen(!open);
            }
          }}
          size={{
            xs: 6
          }}>
          { renderFilters() }
        </Grid>
        { use_quick_fields.map((field, index) => {
          let row_width: number = quick_fields_width;
          if (index + 1 > full) {
            row_width = remainder;
          }
          let xs: 12 | 6 | 4 | 3 = 3;
          switch (row_width) {
            case 1: xs = 12; break;
            case 2: xs = 6; break;
            case 3: xs = 4; break;
          }
          return renderOption(field, xs, use_filter, true);
        })}
        { renderCommonQuickFields() }
        { renderExtras() }
      </Grid>
    );
  }
}
